import axios from 'axios';
import { useAuthStore } from '../state/auth';
// ApiError class for compatibility
export class ApiError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ApiError';
    }
}
// Create the singleton axios instance
const createApiClient = (clientConfig) => {
    const instance = axios.create({
        headers: {
            'Content-Type': 'application/json',
        },
        timeout: 30000, // 30 second timeout
    });
    // Request interceptor for auth and dynamic base URL
    instance.interceptors.request.use((config) => {
        const state = useAuthStore.getState();
        const { accessToken, apiUrl } = state;
        // Set base URL dynamically from auth store
        config.baseURL = apiUrl || 'http://localhost:4000';
        // Add auth token if available
        if (accessToken && config.headers) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        // Log request in development
        if (process.env.NODE_ENV === 'development') {
            console.log(`[API Request] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
        }
        return config;
    }, (error) => {
        console.error('[API Request Error]', error);
        return Promise.reject(error);
    });
    // Response interceptor for error handling
    instance.interceptors.response.use(response => {
        // Log response in development
        if (process.env.NODE_ENV === 'development') {
            console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
        }
        return response;
    }, async (error) => {
        const { response, config } = error;
        if (response) {
            // Log error details in development
            if (process.env.NODE_ENV === 'development') {
                console.error(`[API Error] ${config?.method?.toUpperCase()} ${config?.url} - ${response.status}`, response.data);
            }
            // Handle specific error cases
            switch (response.status) {
                case 401:
                    // Unauthorized - token might be expired
                    // Could implement token refresh logic here
                    console.error('Authentication error - user may need to login again');
                    // Clear session if callback provided (for email-copilot)
                    if (clientConfig?.onSessionClear) {
                        clientConfig.onSessionClear();
                    }
                    // Clear auth state and logout
                    const { logout } = useAuthStore.getState();
                    if (clientConfig?.onLogout) {
                        clientConfig.onLogout();
                    }
                    else {
                        logout();
                    }
                    break;
                case 403:
                    // Forbidden - user doesn't have permission
                    console.error('Permission denied for this resource');
                    break;
                case 404:
                    // Not found
                    console.error('Resource not found');
                    break;
                case 422:
                    // Validation error
                    console.error('Validation error', response.data);
                    break;
                case 400:
                    // Bad request - throw ApiError for compatibility
                    if (response.data?.message) {
                        throw new ApiError(response.data.message);
                    }
                    break;
                case 500:
                case 502:
                case 503:
                    // Server errors
                    console.error('Server error - please try again later');
                    break;
            }
        }
        else if (error.request) {
            // Request was made but no response received
            console.error('Network error - please check your connection');
        }
        return Promise.reject(error);
    });
    return instance;
};
// Create the default singleton instance
export const apiClient = createApiClient();
// Export createApiClient for custom configurations
export { createApiClient };
// Hook for React components (for consistency with existing code)
export const useRestApi = () => apiClient;
// Utility function to extract error message
export const getApiErrorMessage = (error) => {
    if (axios.isAxiosError(error)) {
        // Check for various error message formats
        if (error.response?.data?.message) {
            return error.response.data.message;
        }
        if (error.response?.data?.error) {
            return error.response.data.error;
        }
        if (error.response?.data?.errors) {
            // Handle validation errors (array of errors)
            const errors = error.response.data.errors;
            if (Array.isArray(errors)) {
                return errors.join(', ');
            }
            // Handle validation errors (object of field errors)
            if (typeof errors === 'object') {
                return Object.values(errors).flat().join(', ');
            }
        }
        // Fallback to status text
        if (error.response?.statusText) {
            return error.response.statusText;
        }
    }
    // Generic error message
    return error.message || 'An unexpected error occurred';
};
// Utility function to check if error is a network error
export const isNetworkError = (error) => {
    return axios.isAxiosError(error) && !error.response;
};
// Utility function to check if error is an auth error
export const isAuthError = (error) => {
    return axios.isAxiosError(error) && error.response?.status === 401;
};
